import { Button, Flex, IconButton, Select, Spinner, Text } from '@radix-ui/themes'
import { ChevronLeftIcon, ChevronRightIcon, ResetIcon } from '@radix-ui/react-icons'
import { formatEpisodeRange, formatInstant } from './format'
import { canShiftForward, WINDOW_DAYS, type LeakV0Window } from './chartWindow'
import type { StationLeakV0Episode } from './useLeakV0Episodes'

interface LeakV0ChartToolbarProps {
  fetched: LeakV0Window
  zoom: LeakV0Window | null
  episodes: StationLeakV0Episode[]
  selectedEpisodeId: string | null
  fetching: boolean
  onShift: (direction: -1 | 1) => void
  onSelectEpisode: (episodeId: string) => void
  onResetZoom: () => void
}

const NO_EPISODE_SELECTED = ''

const rangeLabel = ({ from, to }: LeakV0Window): string => `${formatInstant(from)} → ${formatInstant(to)}`

export const LeakV0ChartToolbar = ({
  fetched,
  zoom,
  episodes,
  selectedEpisodeId,
  fetching,
  onShift,
  onSelectEpisode,
  onResetZoom
}: LeakV0ChartToolbarProps) => (
  <Flex align="center" gap="3" wrap="wrap">
    <Flex align="center" gap="1">
      <IconButton
        size="1"
        variant="soft"
        color="gray"
        aria-label="Shift the window back one week"
        onClick={() => onShift(-1)}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        size="1"
        variant="soft"
        color="gray"
        aria-label="Shift the window forward one week"
        disabled={!canShiftForward(fetched)}
        onClick={() => onShift(1)}
      >
        <ChevronRightIcon />
      </IconButton>
    </Flex>

    <Select.Root
      size="1"
      value={selectedEpisodeId ?? NO_EPISODE_SELECTED}
      disabled={episodes.length === 0}
      onValueChange={onSelectEpisode}
    >
      <Select.Trigger
        variant="soft"
        color="gray"
        placeholder={episodes.length === 0 ? 'No episodes' : 'Jump to episode'}
      />
      <Select.Content>
        {episodes.map((episode) => (
          <Select.Item key={episode.id} value={episode.id}>
            {formatEpisodeRange(episode)}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>

    <Text size="1" color="gray">
      {`${WINDOW_DAYS} days · ${rangeLabel(fetched)} · litres`}
    </Text>

    {zoom ? (
      <Flex align="center" gap="2">
        <Text size="1" color="gray">
          {`Zoomed · ${rangeLabel(zoom)}`}
        </Text>
        <Button size="1" variant="soft" color="gray" onClick={onResetZoom}>
          <ResetIcon />
          Reset zoom
        </Button>
      </Flex>
    ) : null}

    {fetching ? <Spinner size="1" /> : null}
  </Flex>
)
